<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Badge,
		Button,
		Input,
		Spinner,
		Label,
		Avatar,
		Modal,
		Toggle
	} from 'flowbite-svelte';
	import PageHeader from '../components/PageHeader.svelte';
	import AbstractDropdown from '../components/AbstractDropdown.svelte';
	import { AbstractDatepicker, Panel, RoutedButton, SortableTableHeadCell, TableColumnPicker } from '../components';
	import { sortLostWithColumn, isDefaultColumns } from '../components/columnVisibility';
	import { listUsers, listRoles, getUserApplications, adminCreateUser } from '../AppNetwork';
	import { toastSuccess, toastError } from '../toast';
	import { push } from '../abstract-spa-router';
	import { storable } from '../storable';
	import site from '../site';
	import type { User, Role, UserApplication, SortDirection, ColumnOption } from '../AppTypes';
	import { t, t2 } from '../i18n';

	/** Badge colour union accepted by flowbite-svelte Badge. */
	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'pink' | 'blue' | 'gray' | 'primary';

	/** Loaded users on current page. */
	let users: User[] = $state([]);
	/** Total user count for pagination. */
	let total = $state(0);
	/** Whether loading. */
	let loading = $state(true);
	/** Error text. */
	let error = $state('');
	/** Search text input. */
	let search = $state('');
	/** Current page (0-indexed). */
	let page = $state(0);
	/** Page size. */
	const limit = 20;

	/** Map of userId → application names for the current page. */
	let userAppsMap: Record<number, string[]> = $state({});

	// ── Filters ───────────────────────────────────────────────────

	/** All available roles for role-filter dropdown + editor multi-select. */
	let allRoles: Role[] = $state([]);
	/** Selected role ID for filtering (empty string = all). */
	let filterRoleId = $state('');
	/** Filter: signup date from. */
	let filterDateFrom = $state('');
	/** Filter: signup date to. */
	let filterDateTo = $state('');
	/** Filter: passwordless state ('', 'true', 'false'). */
	let filterPasswordless = $state('');

	// ── Optional columns ──────────────────────────────────────────

	/**
	 * Every column the table can show, in the order it renders them.
	 *
	 * All of them are toggleable. Only the identity column is not here: it is
	 * the one that says *which* user a row is about, so a table without it is a
	 * grid of facts belonging to nobody. The row's edit action stays for the
	 * same reason — it is the row's affordance, not a column of data.
	 *
	 * Most keys are also server-side sort keys whitelisted in
	 * `USER_SORT_COLUMNS`; `applications` and `roles` are the exceptions, since
	 * neither is a column on the user table to order by.
	 */
	const allColumns: ColumnOption[] = [
		{ key: 'email', name: t2('Email'), icon: 'fa-solid fa-envelope' },
		{ key: 'status', name: t2('Status'), icon: 'fa-solid fa-circle-check' },
		{ key: 'applications', name: t2('Applications'), icon: 'fa-solid fa-cube' },
		{ key: 'roles', name: t2('Roles'), icon: 'fa-solid fa-user-tag' },
		{ key: 'company', name: t2('Company'), icon: 'fa-solid fa-building' },
		{ key: 'location', name: t2('Location'), icon: 'fa-solid fa-location-dot' },
		{ key: 'authProvider', name: t2('Auth Provider'), icon: 'fa-solid fa-right-to-bracket' },
		{ key: 'twoFactorEnabled', name: t2('2FA'), icon: 'fa-solid fa-shield-halved' },
		{ key: 'newsletterOptOut', name: t2('Newsletter'), icon: 'fa-solid fa-envelope-open-text' },
		{ key: 'userUUID', name: t2('UUID'), icon: 'fa-solid fa-fingerprint' },
		{ key: 'created', name: t2('Joined'), icon: 'fa-solid fa-calendar-plus' },
		{ key: 'lastLogin', name: t2('Last Login'), icon: 'fa-solid fa-clock-rotate-left' }
	];

	/** The arrangement the table opens with, and the one `Reset` returns to. */
	const defaultColumns = ['email', 'status', 'applications', 'roles', 'created', 'lastLogin'];

	/**
	 * Which columns are shown, remembered per browser.
	 *
	 * A `.v2` key because the stored value changed meaning: it used to list the
	 * *extra* columns a reader had added, so an old `[]` — by far the most
	 * common stored value — would now read as a table with every column hidden.
	 */
	const visibleColumns = storable<string[]>('users.columns.v2', defaultColumns);

	/** Whether a given column is currently shown. */
	function showing(key: string): boolean {
		return $visibleColumns.includes(key);
	}

	/**
	 * Fixed column widths apply only to the default arrangement.
	 *
	 * The percentages are tuned for those six columns and add up to the whole
	 * table; keeping them while columns are added or removed would leave the
	 * rest fighting over whatever rounding left behind. Without them the browser
	 * lays the table out itself, and `.dataTable` already lets cells wrap and
	 * break long values so it stays within the page for any sane arrangement.
	 */
	function baseWidth(width: string): string {
		return isDefaultColumns($visibleColumns, defaultColumns) ? width : '';
	}

	/**
	 * `truncate` for the default arrangement, wrapping for every other.
	 *
	 * Truncation needs a width to truncate against: `truncate` carries
	 * `white-space: nowrap`, so in an auto-layout table with no fixed widths it
	 * does the opposite of what it says — the cell grows to fit the longest
	 * address on the page (measured at 396px against the 187px the same cell
	 * needs when wrapped) and drags the table into a sideways scroll. With the
	 * percentages gone, letting the text wrap is what keeps it in its column.
	 */
	function clipOrWrap(): string {
		return isDefaultColumns($visibleColumns, defaultColumns) ? 'truncate' : '';
	}

	/**
	 * Apply a change to the visible set.
	 *
	 * Nearly every column is already in the rows the page is holding, so showing
	 * one is a rendering change and nothing more. Two things do need the server:
	 * a dropped sort, which reorders the whole list, and the applications
	 * column, whose contents are a separate request per user rather than a field
	 * on the row — which is also why hiding it stops those requests entirely.
	 */
	function handleColumnsChange(next: string[]): void {
		const sortDropped = sortLostWithColumn(allColumns, next, sortBy);
		const applicationsAppeared = next.includes('applications') && !showing('applications');

		/* Written explicitly rather than left to the binding: the loads below
		   read the store back, and must not race the bound write. */
		visibleColumns.set(next);

		/* Fall back to the default order when the column it sorted by is gone. */
		if (sortDropped) {
			sortBy = '';
			sortDir = 'desc';
			page = 0;
			loadUsers();
			return;
		}

		/* Fetch the application names the page has not had to load until now. */
		if (applicationsAppeared) loadUserApps();
	}

	// ── Sorting ───────────────────────────────────────────────────

	/** Active server-side sort key (`''` = server default, newest first). */
	let sortBy = $state('');
	/** Active server-side sort direction. */
	let sortDir: SortDirection = $state('desc');

	/** Switch the sort column/direction, reset to the first page, and refetch. */
	function handleSort(key: string, dir: SortDirection): void {
		sortBy = key;
		sortDir = dir;
		page = 0;
		loadUsers();
	}

	// ── Data loading ──────────────────────────────────────────────

	/** Load the user list from the server with current filters. */
	async function loadUsers(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch the filtered user list and their associated applications. */
		try {
			const result = await listUsers({
				limit,
				offset: page * limit,
				search: search || undefined,
				roleId: filterRoleId ? parseInt(filterRoleId) : undefined,
				dateFrom: filterDateFrom || undefined,
				dateTo: filterDateTo || undefined,
				passwordless: filterPasswordless === 'true' ? true : filterPasswordless === 'false' ? false : undefined,
				sortBy: sortBy || undefined,
				sortDir: sortBy ? sortDir : undefined
			});
			users = result.items;
			total = result.total;
			/* One request per row — not worth making for a column nobody is looking at. */
			if (showing('applications')) await loadUserApps();
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load users';
		} finally {
			loading = false;
		}
	}

	/** Load application names for all users on the current page. */
	async function loadUserApps(): Promise<void> {
		const map: Record<number, string[]> = {};
		await Promise.all(
			users.map(async (u) => {
				try {
					const apps: UserApplication[] = await getUserApplications(u.id);
					map[u.id] = apps.filter((a) => a.application).map((a) => a.application!.applicationName);
				} catch {
					map[u.id] = [];
				}
			})
		);
		userAppsMap = map;
	}

	/** Load all roles (for filter dropdown and editor multi-select). */
	async function loadRoles(): Promise<void> {
		try {
			allRoles = await listRoles();
		} catch {
			/* non-critical — role filter just won't be populated */
		}
	}

	// ── Search / Filter ───────────────────────────────────────────

	/** Reset page and reload when search or filters change. */
	function handleSearch(): void {
		page = 0;
		loadUsers();
	}

	/** Clear all filters and reload. */
	function clearFilters(): void {
		search = '';
		filterRoleId = '';
		filterDateFrom = '';
		filterDateTo = '';
		filterPasswordless = '';
		page = 0;
		loadUsers();
	}

	/** Whether any filter is active. */
	let hasFilters = $derived(search || filterRoleId || filterDateFrom || filterDateTo || filterPasswordless);

	// ── Create User Modal ────────────────────────────────────────

	let showCreateModal = $state(false);
	let createEmail = $state('');
	let createPassword = $state('');
	let createPasswordless = $state(false);
	let createFirstName = $state('');
	let createLastName = $state('');
	let createActive = $state(true);
	let createVerified = $state(true);
	let isCreating = $state(false);

	/** Whether the form is valid for submission. */
	let canCreate = $derived(createEmail.trim() && (createPasswordless || createPassword));

	/** Reset create form fields. */
	function resetCreateForm(): void {
		createEmail = '';
		createPassword = '';
		createPasswordless = false;
		createFirstName = '';
		createLastName = '';
		createActive = true;
		createVerified = true;
	}

	/** Create a new user via the admin endpoint. */
	async function handleCreateUser(): Promise<void> {
		if (!canCreate) return;
		isCreating = true;
		/** Submit the new user and navigate to the edit page on success. */
		try {
			const user = await adminCreateUser({
				email: createEmail.trim(),
				password: createPasswordless ? undefined : createPassword,
				firstName: createFirstName.trim() || undefined,
				lastName: createLastName.trim() || undefined,
				isActive: createActive,
				isVerified: createVerified
			});
			toastSuccess(t2(`User ${user.email} created.`));
			showCreateModal = false;
			resetCreateForm();
			push(`/Users/${user.id}`);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to create user'));
		} finally {
			isCreating = false;
		}
	}

	// ── Helpers ───────────────────────────────────────────────────

	/**
	 * Split a timestamp into its date and time halves.
	 *
	 * The combined string ("Jan 5, 2026, 02:30 PM") is wide enough that two of
	 * these columns dominated the table, and wrapping broke it at whatever
	 * character happened to land on the edge. Stacking the halves keeps the
	 * column near the width of the date alone and puts the break where it reads.
	 */
	function splitDate(dateString: string): { date: string; time: string } {
		const value = new Date(dateString);
		return {
			date: value.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
			time: value.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
		};
	}

	/** Get display-friendly role names from a user's roles array. */
	function getUserRoleNames(user: User): string[] {
		if (!user.roles || user.roles.length === 0) return [];
		return user.roles.map((r) => r.name);
	}

	/**
	 * Human-readable name for an auth provider.
	 *
	 * `local` covers both password and email-OTP accounts — the column says how
	 * the account is held, not how the last sign-in happened — and `imported`
	 * marks a dormant row that has never been claimed.
	 */
	function authProviderLabel(provider: string): string {
		const labels: Record<string, string> = {
			local: t2('Local'),
			google: t2('Google'),
			apple: t2('Apple'),
			microsoft: t2('Microsoft'),
			imported: t2('Imported')
		};
		return labels[provider] ?? provider;
	}

	/** Badge colour per auth provider, so the column reads at a glance. */
	function authProviderColor(provider: string): BadgeColor {
		const colors: Record<string, BadgeColor> = {
			local: 'gray',
			google: 'blue',
			apple: 'indigo',
			microsoft: 'green',
			imported: 'purple'
		};
		return colors[provider] ?? 'gray';
	}

	/** Rotating badge colours for role pills. */
	const roleBadgeColors: BadgeColor[] = ['indigo', 'purple', 'blue', 'green', 'yellow', 'pink'];
	/** Get badge colour by index (wraps). */
	function roleBadgeColor(index: number): BadgeColor {
		return roleBadgeColors[index % roleBadgeColors.length];
	}

	/** Role-filter dropdown items. */
	let roleFilterOptions = $derived([
		{ value: '', name: t2('All Roles'), icon: 'fa-solid fa-users' },
		...allRoles.map((r) => ({ value: String(r.id), name: r.name, icon: 'fa-solid fa-user-tag' }))
	]);

	/** Passwordless filter dropdown items. */
	const passwordlessFilterOptions = [
		{ value: '', name: t2('All auth types'), icon: 'fa-solid fa-list' },
		{ value: 'true', name: t2('Passwordless'), icon: 'fa-solid fa-fingerprint' },
		{ value: 'false', name: t2('Has password'), icon: 'fa-solid fa-key' }
	];

	onMount(() => {
		loadUsers();
		loadRoles();
	});
</script>

<PageHeader title={$t("Users")} description={$t("Manage user accounts and permissions")} />

<div class="-mt-4 mb-4 flex justify-end gap-2">
	<Button color="primary" onclick={() => { resetCreateForm(); showCreateModal = true; }}>
		<i class="fa-solid fa-user-plus mr-2"></i> {$t("Create User")}
	</Button>
	<RoutedButton color="alternative" href="/Users/Import">
		<i class="fa-solid fa-file-import mr-2"></i> {$t("Import Users")}
	</RoutedButton>
</div>

<!-- Filters -->
<Panel title={$t("Filters")} faIcon="filter" class="mb-6">
	<div class="flex flex-wrap items-center gap-3">
		<div class="max-w-xs flex-1">
			<Input
				bind:value={search}
				placeholder={$t("Search by name or email...")}
				onkeypress={(e) => e.key === 'Enter' && handleSearch()}
			>
				{#snippet left()}
								<i  class="fa-solid fa-search text-gray-400"></i>
							{/snippet}
			</Input>
		</div>
		<AbstractDropdown items={roleFilterOptions} bind:value={filterRoleId} onchange={() => { handleSearch(); }} class="w-44" />
		<div class="w-40">
			<AbstractDatepicker
				mode="date"
				bind:stringValue={filterDateFrom}
				showRelativeDate={false}
				onchange={handleSearch}
			/>
		</div>
		<span class="text-gray-400">&mdash;</span>
		<div class="w-40">
			<AbstractDatepicker
				mode="date"
				bind:stringValue={filterDateTo}
				showRelativeDate={false}
				onchange={handleSearch}
			/>
		</div>
		<AbstractDropdown items={passwordlessFilterOptions} bind:value={filterPasswordless} onchange={() => { handleSearch(); }} class="w-48" />
		<Button onclick={handleSearch}>{$t("Search")}</Button>
		{#if hasFilters}
			<Button size="sm" color="alternative" onclick={clearFilters}>
				<i class="fa-solid fa-xmark me-1"></i>{$t("Clear")}
			</Button>
		{/if}
		<!-- Pushed to the far end: it shapes the table rather than the query,
		     so it does not belong in the run of filters that narrow the list. -->
		<div class="ms-auto">
			<TableColumnPicker
				columns={allColumns}
				defaults={defaultColumns}
				bind:visible={$visibleColumns}
				onchange={handleColumnsChange}
			/>
		</div>
	</div>
</Panel>

<!-- Shared placeholder for an empty optional cell, so every one of them
     renders the same dash at the same weight. -->
{#snippet blank()}
	<span class="text-xs text-gray-400">&mdash;</span>
{/snippet}

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="8" />
	</div>
{:else if error}
	<div class="py-12 text-center">
		<i class="fa-solid fa-exclamation-triangle mb-4 text-4xl text-red-500"></i>
		<p class="text-gray-500 dark:text-gray-400">{error}</p>
		<Button class="mt-4" onclick={loadUsers}>{$t("Retry")}</Button>
	</div>
{:else if users.length === 0}
	<div class="py-12 text-center">
		<i class="fa-solid fa-users mb-4 text-4xl text-gray-300 dark:text-gray-600"></i>
		<p class="text-gray-500 dark:text-gray-400">
			{hasFilters ? $t('No users match your filters') : $t('No users found')}
		</p>
	</div>
{:else}
	<Table divClass="widget !p-1 relative" class="dataTable">
		<TableHead class="bg-gray-50 dark:bg-gray-700">
			<!-- The identity column is the one thing that cannot be hidden: without it
			     a row is a set of facts belonging to nobody. -->
			<SortableTableHeadCell key="name" class={baseWidth('w-[13%]')} {sortBy} {sortDir} onsort={handleSort}>{$t("User")}</SortableTableHeadCell>
			{#if showing('email')}
				<SortableTableHeadCell key="email" class={baseWidth('w-[17%]')} {sortBy} {sortDir} onsort={handleSort}>{$t("Email")}</SortableTableHeadCell>
			{/if}
			{#if showing('status')}
				<SortableTableHeadCell key="status" class={baseWidth('w-[20%]')} {sortBy} {sortDir} onsort={handleSort}>{$t("Status")}</SortableTableHeadCell>
			{/if}
			{#if showing('applications')}
				<TableHeadCell class={baseWidth('w-[14%]')}>{$t("Applications")}</TableHeadCell>
			{/if}
			{#if showing('roles')}
				<TableHeadCell class={baseWidth('w-[14%]')}>{$t("Roles")}</TableHeadCell>
			{/if}
			{#if showing('company')}
				<SortableTableHeadCell key="company" {sortBy} {sortDir} onsort={handleSort}>{$t("Company")}</SortableTableHeadCell>
			{/if}
			{#if showing('location')}
				<SortableTableHeadCell key="location" {sortBy} {sortDir} onsort={handleSort}>{$t("Location")}</SortableTableHeadCell>
			{/if}
			{#if showing('authProvider')}
				<SortableTableHeadCell key="authProvider" {sortBy} {sortDir} onsort={handleSort}>{$t("Auth Provider")}</SortableTableHeadCell>
			{/if}
			{#if showing('twoFactorEnabled')}
				<SortableTableHeadCell key="twoFactorEnabled" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>{$t("2FA")}</SortableTableHeadCell>
			{/if}
			{#if showing('newsletterOptOut')}
				<SortableTableHeadCell key="newsletterOptOut" {sortBy} {sortDir} onsort={handleSort}>{$t("Newsletter")}</SortableTableHeadCell>
			{/if}
			{#if showing('userUUID')}
				<SortableTableHeadCell key="userUUID" {sortBy} {sortDir} onsort={handleSort}>{$t("UUID")}</SortableTableHeadCell>
			{/if}
			{#if showing('created')}
				<SortableTableHeadCell key="created" class={baseWidth('w-[9%]')} {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
					{$t("Joined")}
				</SortableTableHeadCell>
			{/if}
			{#if showing('lastLogin')}
				<SortableTableHeadCell key="lastLogin" class={baseWidth('w-[9%]')} {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
					{$t("Last Login")}
				</SortableTableHeadCell>
			{/if}
			<TableHeadCell class="w-px !px-2"><span class="sr-only">{$t("Actions")}</span></TableHeadCell>
		</TableHead>
		<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
			{#each users as user (user.id)}
				<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
					<TableBodyCell class="font-medium text-gray-900 dark:text-white">
						{@const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()}
						<div class="flex items-center gap-2">
							<Avatar
								src={user.imageName ? `${site.backend.baseURL}/uploads/avatars/${user.imageName}` : ''}
								size="sm"
								class="shrink-0 rounded-full"
							/>
							<!-- min-w-0 is what lets `truncate` clip inside a flex row; without
							     it the child keeps its content width and the column grows. -->
							<div class="min-w-0">
								<div class={clipOrWrap()} title={fullName}>{fullName}</div>
								{#if user.username}
									<div class="{clipOrWrap()} text-xs text-gray-500 dark:text-gray-400" title="@{user.username}">
										@{user.username}
									</div>
								{/if}
							</div>
						</div>
					</TableBodyCell>
					{#if showing('email')}
						<TableBodyCell>
							<div class={clipOrWrap()} title={user.email}>{user.email}</div>
						</TableBodyCell>
					{/if}
					{#if showing('status')}
					<TableBodyCell>
						<div class="flex flex-wrap gap-1">
							{#if user.isAdmin}
								<Badge color="green" class="text-xs">{$t("Admin")}</Badge>
							{:else}
								<Badge color="gray" class="text-xs">{$t("User")}</Badge>
							{/if}
							{#if user.authProvider === 'imported'}
								<Badge color="purple" class="text-xs">{$t("Imported")}</Badge>
							{/if}
							{#if !user.isActive && user.authProvider !== 'imported'}
								<Badge color="red" class="text-xs">{$t("Inactive")}</Badge>
							{/if}
							{#if !user.isVerified}
								<Badge color="yellow" class="text-xs">{$t("Unverified")}</Badge>
							{/if}
							{#if user.passwordless}
								<Badge color="pink" class="text-xs">{$t("Passwordless")}</Badge>
							{/if}
						</div>
					</TableBodyCell>
					{/if}
					{#if showing('applications')}
						<TableBodyCell>
							<div class="flex flex-wrap gap-1">
								{#each userAppsMap[user.id] || [] as appName (appName)}
									<Badge color="blue" class="text-xs">{appName}</Badge>
								{:else}
									{@render blank()}
								{/each}
							</div>
						</TableBodyCell>
					{/if}
					{#if showing('roles')}
						<TableBodyCell>
							<div class="flex flex-wrap gap-1">
								{#each getUserRoleNames(user) as roleName, i (roleName)}
									<Badge color={roleBadgeColor(i)} class="text-xs">{roleName}</Badge>
								{:else}
									{@render blank()}
								{/each}
							</div>
						</TableBodyCell>
					{/if}
					{#if showing('company')}
						<TableBodyCell class="text-gray-500 dark:text-gray-400">
							{#if user.company}<span title={user.company}>{user.company}</span>{:else}{@render blank()}{/if}
						</TableBodyCell>
					{/if}
					{#if showing('location')}
						<TableBodyCell class="text-gray-500 dark:text-gray-400">
							{#if user.location}<span title={user.location}>{user.location}</span>{:else}{@render blank()}{/if}
						</TableBodyCell>
					{/if}
					{#if showing('authProvider')}
						<TableBodyCell>
							<Badge color={authProviderColor(user.authProvider)} class="text-xs whitespace-nowrap">
								{authProviderLabel(user.authProvider)}
							</Badge>
						</TableBodyCell>
					{/if}
					{#if showing('twoFactorEnabled')}
						<TableBodyCell>
							{#if user.twoFactorEnabled}
								<Badge color="green" class="text-xs">{$t("Enabled")}</Badge>
							{:else}
								<Badge color="gray" class="text-xs">{$t("Disabled")}</Badge>
							{/if}
						</TableBodyCell>
					{/if}
					{#if showing('newsletterOptOut')}
						<TableBodyCell>
							{#if user.newsletterOptOut}
								<Badge color="gray" class="text-xs">{$t("Opted out")}</Badge>
							{:else}
								<Badge color="blue" class="text-xs">{$t("Subscribed")}</Badge>
							{/if}
						</TableBodyCell>
					{/if}
					{#if showing('userUUID')}
						<TableBodyCell class="font-mono text-xs text-gray-500 dark:text-gray-400">
							{user.userUUID}
						</TableBodyCell>
					{/if}
					{#if showing('created')}
						<TableBodyCell class="text-gray-500 dark:text-gray-400">
							{@const joined = splitDate(user.created)}
							<div class="whitespace-nowrap">{joined.date}</div>
							<div class="whitespace-nowrap text-xs opacity-75">{joined.time}</div>
						</TableBodyCell>
					{/if}
					{#if showing('lastLogin')}
						<TableBodyCell class="text-gray-500 dark:text-gray-400">
							{#if user.lastLoginDate}
								{@const lastLogin = splitDate(user.lastLoginDate)}
								<div class="whitespace-nowrap">{lastLogin.date}</div>
								<div class="whitespace-nowrap text-xs opacity-75">{lastLogin.time}</div>
							{:else}
								<span class="whitespace-nowrap">{$t('Never')}</span>
							{/if}
						</TableBodyCell>
					{/if}
					<TableBodyCell class="!px-2">
						<!-- Icon only: the column is the narrowest thing in the table, and a
						     text label here costs more width than the action is worth. -->
						<RoutedButton
							size="xs"
							color="alternative"
							href="/Users/{user.id}"
							class="!p-2"
							title={$t('Edit')}
							aria-label={$t('Edit')}
						>
							<i class="fa-solid fa-pen-to-square h-3 w-3"></i>
						</RoutedButton>
					</TableBodyCell>
				</TableBodyRow>
			{/each}
		</TableBody>
	</Table>

	<!-- Pagination -->
	{#if total > limit}
		<div class="mt-4 flex items-center justify-between">
			<p class="text-sm text-gray-500 dark:text-gray-400">
				{$t("Showing {{from}} to {{to}} of {{total}} users", { from: page * limit + 1, to: Math.min((page + 1) * limit, total), total })}
			</p>
			<div class="flex gap-2">
				<Button
					disabled={page === 0}
					onclick={() => {
						page--;
						loadUsers();
					}}
					size="sm"
				>
					<i class="fa-solid fa-chevron-left mr-1"></i>
					{$t("Previous")}
				</Button>
				<Button
					disabled={(page + 1) * limit >= total}
					onclick={() => {
						page++;
						loadUsers();
					}}
					size="sm"
				>
					{$t("Next")}
					<i class="fa-solid fa-chevron-right ml-1"></i>
				</Button>
			</div>
		</div>
	{/if}
{/if}

<!-- Create User Modal -->
<Modal bind:open={showCreateModal} title={$t("Create User")} size="sm" autoclose={false}>
	<div class="space-y-4">
		<div>
			<Label class="mb-1">{$t("Email")} *</Label>
			<Input bind:value={createEmail} type="email" placeholder={$t("user@example.com")} />
		</div>
		<Toggle bind:checked={createPasswordless}>
			<span class="flex items-center gap-1.5">
				<i class="fa-solid fa-envelope"></i> {$t("Passwordless")}
			</span>
		</Toggle>
		{#if !createPasswordless}
			<div>
				<Label class="mb-1">{$t("Password")} *</Label>
				<Input bind:value={createPassword} type="password" placeholder={$t("Password")} />
			</div>
		{/if}
		<div class="grid grid-cols-2 gap-4">
			<div>
				<Label class="mb-1">{$t("First Name")}</Label>
				<Input bind:value={createFirstName} placeholder={$t("First name")} />
			</div>
			<div>
				<Label class="mb-1">{$t("Last Name")}</Label>
				<Input bind:value={createLastName} placeholder={$t("Last name")} />
			</div>
		</div>
		<div class="flex gap-6 border-t border-gray-200 pt-4 dark:border-gray-700">
			<Toggle bind:checked={createActive}>{$t("Active")}</Toggle>
			<Toggle bind:checked={createVerified}>{$t("Verified")}</Toggle>
		</div>
	</div>
	{#snippet footer()}
		<div class="flex w-full justify-end gap-2">
			<Button color="alternative" onclick={() => { showCreateModal = false; }}>{$t("Cancel")}</Button>
			<Button
				color="primary"
				onclick={handleCreateUser}
				disabled={isCreating || !canCreate}
			>
				{#if isCreating}<Spinner size="4" class="mr-2" />{/if}
				{$t("Create User")}
			</Button>
		</div>
	{/snippet}
</Modal>
